import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import { SearchRequest } from '../../services/api/interfaces/ApiRequest'
import { announcementList, announcementCreate, announcementUpdate, announcementDelete } from '../../services/api'

interface AnnouncementContextState extends BaseContextState {
  announcements: Array<any> | null,
  announcement: any | null
}

export interface AnnouncementContextData extends AnnouncementContextState {
  resetErrors: Function,
  requestAnnouncementList: (args: SearchRequest) => Promise<any>,
  requestAnnouncementFind: (args: SearchRequest) => void,
  requestAnnouncementCreate: (args: any) => Promise<any>,
  requestAnnouncementUpdate: (args: any) => Promise<any>,
  requestAnnouncementDelete: (args: any) => Promise<any>
}

// Create context
const { Provider, Consumer } = createContext({} as AnnouncementContextData)

// Export consumer
export const AnnouncementConsumer = Consumer

export class AnnouncementProvider extends BaseContextProvider {
  state: AnnouncementContextState = {
    announcements: null,
    announcement: null
  }

  lastListPayload: SearchRequest | null = null

  /**
   * Make api request for announcement list
   * 
   * @param {SearchRequest} payload 
   */
  requestAnnouncementList = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        this.lastListPayload = payload
        const response = await announcementList(payload)
  
        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'announcements'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for specie finder
   * 
   * @param {SearchRequest} payload 
   */
  requestAnnouncementFind = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null,
      announcement: null
    }, async () => {
      payload.options = {
        ...payload.options,
        _single: true
      }
      const response = await announcementList(payload)

      // Process our response
      this.processResponse(response, { 
        mappings: {
          destination: 'announcement'
        }
      })
    })
  }

  /**
   * Make api request for announcement create
   * 
   * @param {any} payload 
   */
  requestAnnouncementCreate = (payload: any) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        announcement: null,
      }, async () => {
        let response = await announcementCreate(payload)
        this.processResponse(response, {
          mappings: [
            { origin: 'announcement', destination: 'announcement' }
          ]
        })
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for announcement update
   * 
   * @param {any} payload 
   */
  requestAnnouncementUpdate = (payload: any) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await announcementUpdate(payload)
        this.processResponse(response, {
          mappings: [
            { origin: 'announcement', destination: 'announcement' }
          ]
        })
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for announcement delete
   * 
   * @param {any} payload 
   */
  requestAnnouncementDelete = (payload: any) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await announcementDelete(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          this.requestAnnouncementList(this.lastListPayload || {})
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestAnnouncementList: this.requestAnnouncementList,
          requestAnnouncementFind: this.requestAnnouncementFind,
          requestAnnouncementCreate: this.requestAnnouncementCreate,
          requestAnnouncementUpdate: this.requestAnnouncementUpdate,
          requestAnnouncementDelete: this.requestAnnouncementDelete
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
